class QueryBuilder implements PromiseLike<any> {
  private query: Record<string, unknown>;
  constructor(table: string) {
    this.query = { table, operation: 'select', filters: [] };
  }
  select(_columns = '*', options?: { head?: boolean; count?: string }) {
    this.query.head = options?.head;
    return this;
  }
  insert(payload: unknown) {
    this.query.operation = 'insert';
    this.query.payload = payload;
    return this;
  }
  update(payload: unknown) {
    this.query.operation = 'update';
    this.query.payload = payload;
    return this;
  }
  upsert(payload: unknown, options?: { onConflict?: string }) {
    this.query.operation = 'upsert';
    this.query.payload = payload;
    this.query.onConflict = options?.onConflict;
    return this;
  }
  delete() {
    this.query.operation = 'delete';
    return this;
  }
  private filter(op: string, column: string, value: unknown) {
    (this.query.filters as unknown[]).push({ op, column, value });
    return this;
  }
  eq(column: string, value: unknown) {
    return this.filter('eq', column, value);
  }
  in(column: string, value: unknown[]) {
    return this.filter('in', column, value);
  }
  gte(column: string, value: unknown) {
    return this.filter('gte', column, value);
  }
  lte(column: string, value: unknown) {
    return this.filter('lte', column, value);
  }
  not(column: string, _operator: string, value: unknown) {
    return this.filter('neq', column, value);
  }
  order(column: string, options?: { ascending?: boolean }) {
    this.query.order = { column, ascending: options?.ascending ?? true };
    return this;
  }
  limit(value: number) {
    this.query.limit = value;
    return this;
  }
  single() {
    this.query.single = true;
    return this;
  }
  maybeSingle() {
    this.query.maybeSingle = true;
    return this;
  }
  then(
    resolve?: ((value: any) => any) | null,
    reject?: ((reason: unknown) => any) | null
  ): PromiseLike<any> {
    return fetch('/api/data', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(this.query),
    })
      .then((r) => r.json())
      .then(resolve, reject);
  }
}

async function authRequest(path: string, init?: RequestInit) {
  const response = await fetch(`/api/auth/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...init?.headers },
  });
  const payload = await response.json();
  return response.ok
    ? { data: payload, error: null }
    : { data: null, error: { message: payload.error ?? 'Request failed' } };
}

let browserClient: any;

export function createClient(): any {
  if (browserClient) return browserClient;
  browserClient = {
    from: (table: string) => new QueryBuilder(table),
    auth: {
      async getSession() {
        const r = await authRequest('session');
        return { data: { session: r.data?.session ?? null }, error: r.error };
      },
      async getUser() {
        const r = await authRequest('session');
        return { data: { user: r.data?.user ?? null }, error: r.error };
      },
      async signInWithPassword(input: { email: string; password: string }) {
        return authRequest('login', { method: 'POST', body: JSON.stringify(input) });
      },
      async signOut() {
        return authRequest('logout', { method: 'POST' });
      },
      async updateUser(input: { password?: string; currentPassword?: string }) {
        return authRequest('password', { method: 'PATCH', body: JSON.stringify(input) });
      },
      async signUp(..._args: unknown[]) {
        return {
          data: null,
          error: { message: 'Accounts are created by the SACCO administrator.' },
        };
      },
      async resetPasswordForEmail(..._args: unknown[]) {
        return {
          data: null,
          error: { message: 'Contact the SACCO administrator to reset your password.' },
        };
      },
      onAuthStateChange(callback: (event: string, session: any) => void) {
        void authRequest('session').then((r) =>
          callback('INITIAL_SESSION', r.data?.session ?? null)
        );
        return { data: { subscription: { unsubscribe() {} } } };
      },
    },
    functions: {
      async invoke(name: string, options?: { body?: unknown }) {
        const response = await fetch(`/api/functions/${encodeURIComponent(name)}`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(options?.body ?? {}),
        });
        const data = await response.json();
        return { data: response.ok ? data : null, error: response.ok ? null : data };
      },
    },
    storage: {
      from(..._args: unknown[]) {
        return {
          async upload(..._uploadArgs: unknown[]) {
            return {
              data: null,
              error: { message: 'Local document storage is not configured yet.' },
            };
          },
          getPublicUrl(..._urlArgs: unknown[]) {
            return { data: { publicUrl: '' } };
          },
        };
      },
    },
  };
  return browserClient;
}
